// =====================================================================
// Assessment Center — Student Panel
// Data layer for the student-facing attempt flow.
//
// Reads/writes exam/question catalog data and every transactional
// table (exam_attempts, student_answers, essay_reviews, grades)
// through src/shared/grading/attempts-store.ts, the one store shared
// with the instructor panel's grading review flow. `submitAttempt`
// runs real grading via src/shared/grading/grading-service.ts. The
// current student's identity comes from the signed-in session (see
// src/shared/auth) rather than a hardcoded id.
//
// Every exported function here is what a real fetch-based client
// should end up with — see DEPLOYMENT.md.
// =====================================================================
import * as store from "../../../shared/grading/attempts-store";
import { gradeSubmission } from "../../../shared/grading/grading-service";
import { requireSession } from "../../../shared/auth/session";
import type {
  Exam,
  ExamAttempt,
  Question,
  QuestionType,
  StudentAnswer,
  Grade,
} from "../../../shared/types/db";

function currentStudentId(): string {
  return requireSession("student").id;
}

const uuid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `id_${Math.random().toString(36).slice(2)}_${Date.now()}`;

const nowIso = () => new Date().toISOString();
const delay = (ms = 220) => new Promise((r) => setTimeout(r, ms));

// ---------------------------------------------------------------------
// Shared shaping helpers
// ---------------------------------------------------------------------

/** Answer options with `is_correct` stripped — never sent to a student mid-attempt. */
function publicOptions(questionId: string) {
  return store.getMcAnswers(questionId).map(({ id, answer_text, order_index }) => ({
    id,
    answer_text,
    order_index,
  }));
}

function attemptsUsed(examId: string) {
  return store.listAttempts({ examId, studentId: currentStudentId() }).length;
}

function inProgressAttempt(examId: string) {
  return store
    .listAttempts({ examId, studentId: currentStudentId() })
    .find((a) => a.status === "in_progress");
}

function allExamQuestionsFor(examId: string) {
  return store.getExamSections(examId).flatMap((s) => store.getExamQuestions(s.id));
}

// ---------------------------------------------------------------------
// Available Exams
// ---------------------------------------------------------------------
export interface AvailableExam extends Exam {
  question_count: number;
  attempts_used: number;
  attempts_remaining: number;
  has_in_progress_attempt: boolean;
}

export async function listAvailableExams(): Promise<AvailableExam[]> {
  await delay();
  const now = nowIso();
  return store
    .listPublishedExams()
    .filter((e) => !e.start_at || e.start_at <= now)
    .filter((e) => !e.end_at || e.end_at >= now)
    .map((e) => {
      const used = attemptsUsed(e.id);
      return {
        ...e,
        question_count: allExamQuestionsFor(e.id).length,
        attempts_used: used,
        attempts_remaining: Math.max(e.max_attempts - used, 0),
        has_in_progress_attempt: Boolean(inProgressAttempt(e.id)),
      };
    })
    .filter((e) => e.attempts_remaining > 0 || e.has_in_progress_attempt);
}

// ---------------------------------------------------------------------
// My Exams
// ---------------------------------------------------------------------
export interface MyAttempt extends ExamAttempt {
  exam_title: string;
  exam_type: Exam["exam_type"];
  duration_minutes: number;
}

export async function listMyAttempts(): Promise<MyAttempt[]> {
  await delay();
  return store.listAttempts({ studentId: currentStudentId() }).map((a) => {
    const exam = store.getExam(a.exam_id)!;
    return { ...a, exam_title: exam.title, exam_type: exam.exam_type, duration_minutes: exam.duration_minutes };
  });
}

// ---------------------------------------------------------------------
// Taking an exam
// ---------------------------------------------------------------------
export interface RunnerQuestion {
  exam_question_id: string;
  question_id: string;
  type: QuestionType;
  prompt: string;
  points: number;
  options?: { id: string; answer_text: string; order_index: number }[];
  min_words?: number | null;
  max_words?: number | null;
}

export interface RunnerSection {
  id: string;
  title: string;
  instructions: string | null;
  section_type: QuestionType;
  questions: RunnerQuestion[];
}

export interface AttemptRunner {
  attempt: ExamAttempt;
  exam: Exam;
  sections: RunnerSection[];
  answers: Record<string, { selected_answer_id: string | null; essay_answer_text: string | null }>;
}

function buildRunnerSections(examId: string): RunnerSection[] {
  return store.getExamSections(examId).map((s) => ({
    id: s.id,
    title: s.title,
    instructions: s.instructions,
    section_type: s.section_type,
    questions: store.getExamQuestions(s.id).map((eq) => {
      const q = store.getQuestion(eq.question_id)!;
      const essay = store.getEssayDetail(q.id);
      return {
        exam_question_id: eq.id,
        question_id: q.id,
        type: q.type,
        prompt: q.prompt,
        points: store.pointsForExamQuestion(eq),
        options: q.type === "multiple_choice" ? publicOptions(q.id) : undefined,
        min_words: essay?.min_words,
        max_words: essay?.max_words,
      };
    }),
  }));
}

/** Starts a new attempt, or resumes an existing in-progress one for this exam. */
export async function startAttempt(examId: string): Promise<AttemptRunner> {
  await delay();
  const exam = store.getExam(examId);
  if (!exam) throw new Error("Exam not found.");

  let attempt = inProgressAttempt(examId);
  if (!attempt) {
    if (attemptsUsed(examId) >= exam.max_attempts) {
      throw new Error("You've used all of your attempts for this exam.");
    }
    attempt = store.insertAttempt({
      id: uuid(),
      exam_id: examId,
      student_id: currentStudentId(),
      attempt_number: attemptsUsed(examId) + 1,
      status: "in_progress",
      started_at: nowIso(),
      submitted_at: null,
      created_at: nowIso(),
    });
  }

  return getAttemptRunner(attempt.id);
}

export async function getAttemptRunner(attemptId: string): Promise<AttemptRunner> {
  await delay();
  const attempt = store.getAttempt(attemptId);
  if (!attempt) throw new Error("Attempt not found.");
  const exam = store.getExam(attempt.exam_id)!;

  const answers: AttemptRunner["answers"] = {};
  store.listStudentAnswers(attemptId).forEach((sa) => {
    answers[sa.exam_question_id] = {
      selected_answer_id: sa.selected_answer_id,
      essay_answer_text: sa.essay_answer_text,
    };
  });

  return { attempt, exam, sections: buildRunnerSections(exam.id), answers };
}

export interface SaveAnswerInput {
  selected_answer_id?: string | null;
  essay_answer_text?: string | null;
}

/** Auto-save: upserts a single answer for the current attempt. */
export async function saveAnswer(
  attemptId: string,
  examQuestionId: string,
  input: SaveAnswerInput
): Promise<void> {
  await delay(120); // shorter than page-load delay — this fires often
  const attempt = store.getAttempt(attemptId);
  if (!attempt) throw new Error("Attempt not found.");
  if (attempt.status !== "in_progress") {
    throw new Error("This attempt is no longer in progress.");
  }
  store.upsertStudentAnswer({ exam_attempt_id: attemptId, exam_question_id: examQuestionId, ...input });
}

export interface SubmitSummary {
  attempt_id: string;
  answered_count: number;
  question_count: number;
  status: ExamAttempt["status"];
}

/**
 * Marks the attempt submitted and runs grading immediately:
 *   - Multiple Choice exam → graded in full right here.
 *   - Essay / Mixed exam   → MC portion (if any) graded now, essay
 *     answers queued for instructor review; attempt stays "submitted"
 *     (== Waiting for Review) until every review is complete.
 * See ../../shared/grading-service.ts for the actual rules.
 */
export async function submitAttempt(attemptId: string): Promise<SubmitSummary> {
  await delay();
  const attempt = store.getAttempt(attemptId);
  if (!attempt) throw new Error("Attempt not found.");
  if (attempt.status !== "in_progress") {
    throw new Error("This attempt has already been submitted.");
  }

  const questionCount = allExamQuestionsFor(attempt.exam_id).length;
  const answeredCount = store
    .listStudentAnswers(attemptId)
    .filter((sa) => sa.selected_answer_id || (sa.essay_answer_text && sa.essay_answer_text.trim())).length;

  const graded = gradeSubmission(attemptId);

  return { attempt_id: attemptId, answered_count: answeredCount, question_count: questionCount, status: graded.status };
}

// ---------------------------------------------------------------------
// Results (post-submission, read-only)
// ---------------------------------------------------------------------
export type ReviewState = "not_applicable" | "pending" | "completed";

export interface ResultAnswer {
  exam_question_id: string;
  type: QuestionType;
  prompt: string;
  points_possible: number;
  selected_option_text: string | null;
  essay_answer_text: string | null;
  answered: boolean;
  is_correct: boolean | null; // multiple choice only, null until graded
  points_awarded: number | null; // known immediately for MC, after review for essay
  review_state: ReviewState;
  reviewer_feedback: string | null;
}

export interface ResultSection {
  id: string;
  title: string;
  section_type: QuestionType;
  answers: ResultAnswer[];
}

export interface AttemptResult {
  attempt: ExamAttempt;
  exam: Exam;
  sections: ResultSection[];
  grade: Grade | null; // null until fully graded (essay/mixed waits on instructor review)
}

export async function getResult(attemptId: string): Promise<AttemptResult> {
  await delay();
  const attempt = store.getAttempt(attemptId);
  if (!attempt) throw new Error("Attempt not found.");
  const exam = store.getExam(attempt.exam_id)!;

  const sections: ResultSection[] = store.getExamSections(exam.id).map((s) => ({
    id: s.id,
    title: s.title,
    section_type: s.section_type,
    answers: store.getExamQuestions(s.id).map((eq) => {
      const q = store.getQuestion(eq.question_id) as Question;
      const sa: StudentAnswer | undefined = store.getStudentAnswer(attemptId, eq.id);
      const selectedOption = sa?.selected_answer_id
        ? store.getMcAnswers(q.id).find((a) => a.id === sa.selected_answer_id)
        : undefined;

      let reviewState: ReviewState = "not_applicable";
      let feedback: string | null = null;
      if (q.type === "essay" && sa) {
        const review = store.getEssayReviewByAnswer(sa.id);
        if (review) {
          reviewState = review.status === "completed" ? "completed" : "pending";
          feedback = review.feedback;
        }
      }

      return {
        exam_question_id: eq.id,
        type: q.type,
        prompt: q.prompt,
        points_possible: store.pointsForExamQuestion(eq),
        selected_option_text: selectedOption?.answer_text ?? null,
        essay_answer_text: sa?.essay_answer_text ?? null,
        answered: Boolean(sa?.selected_answer_id || sa?.essay_answer_text?.trim()),
        is_correct: sa?.is_correct ?? null,
        points_awarded: sa?.points_awarded ?? null,
        review_state: reviewState,
        reviewer_feedback: feedback,
      };
    }),
  }));

  return { attempt, exam, sections, grade: store.getGrade(attemptId) ?? null };
}
